import { gql } from "@apollo/client";

export const GET_INVENTORY_USAGE_SUMMARY = gql`
  query getInventoryUsageSummary($startDate: String!, $endDate: String!, $employeeId: ID) {
    getInventoryUsageSummary(startDate: $startDate, endDate: $endDate, employeeId: $employeeId) {
      status
      message
      data {
        categories {
          categoryId
          categoryName
          isScanRequired
          parts
          initialQty
          initialParts
          assignedQty
          assignedParts
          cutParts
          cutOrderCount
          writtenOffQty
          rejectedQty
          remainingParts
        }
        employees {
          employeeId
          employeeName
          categories {
            categoryId
            categoryName
            isScanRequired
            parts
            initialQty
            initialParts
            assignedQty
            assignedParts
            cutParts
            cutOrderCount
            writtenOffQty
            rejectedQty
            remainingParts
          }
        }
      }
    }
  }
`;

export const ADMIN_INVENTORY_DASHBOARD = gql`
  query getAdminInventoryDashboard($employeeId: ID) {
    getAdminInventoryDashboard(employeeId: $employeeId) {
      status
      message
      data {
        summary {
          totalCategories
          totalUsableQuantity
          totalUsableParts
          lowStockCategories
          outOfStockCategories
        }
        categories {
          id
          name
          abbr
          parts
          minStockAdmin
          minStock
          usableQuantity
          isScanRequired
          usableParts
          pendingUsableQuantity
          pendingUsableParts
          effectiveAvailable # optional, if you added this
          stockStatus
          totalItems
          usedUpItems
          averageUsage
          utilizationRate
          assigned
          unassigned
          staleUnassigned
        }
        historyByDate {
          date
          inventoryList {
            categoryId
            catName
            count
            verifiedDate
          }
        }
      }
    }
  }
`;

export const GET_USABLE_INVENTORY_CATEGORIES = gql`
  query getUsableInventoryCategory {
    getUsableInventoryCategory {
      status
      message
      categories {
        id
        name
        abbr
        parts
        usableQuantity
      }
    }
  }
`;

export const EMPLOYEE_LIST = gql`
  query GetEmployeeList($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      employee {
        id
        fullName
      }
    }
  }
`;
export const GET_INVENTORY = gql`
  query GetInventory($data: getInventoryInput) {
    getInventory(data: $data) {
      message
      status
      inventoryList {
        id
        status
        name
        category
        employeeId
        employeeName
        assignedDate
        description
        usage
        quantity
        parts
        isScanRequired
      }
    }
  }
`;
export const ASSIGN_INVENTORY_TO_EMPLOYEE = gql`
  mutation AssignInventoryToEmployee($data: AssignInventoryToEmployeeInput!) {
    assignInventoryToEmployee(data: $data) {
      status
      message
      assignedCount
      assignedQty
      warnings
    }
  }
`;

export const GET_INVENTORY_PURCHASE_HISTORY = gql`
  query getInventoryPurchaseHistory($categoryId: ID!) {
    getInventoryPurchaseHistory(categoryId: $categoryId) {
      id
      date
      dealerName
      quantity
      rate
      mrp
    }
  }
`;

export const GET_INVENTORY_RECONCILIATION_REPORT = gql`
  query getInventoryReconciliationReport(
    $startDate: String!
    $endDate: String!
    $employeeId: ID
  ) {
    getInventoryReconciliationReport(
      startDate: $startDate
      endDate: $endDate
      employeeId: $employeeId
    ) {
      status
      message
      data {
        summary {
          totalEmployees
          totalGapParts
          totalDriftOrders
          totalMismatchOrders
        }
        employees {
          employeeId
          employeeName
          totalOrdersInPeriod
          totalCurrentAssignedQty
          totalCurrentPendingQty
          totalPeriodAssignedQty
          driftOrderCount
          driftOrderIds
          mismatchOrderCount
          forceScanCount
          forceScanRate
          writtenOffCount
          rejectedCount
          mismatchOrders {
            orderId
            bookingId
            bookingDate
            expectedParts
            actualParts
            gapParts
            mismatchType
            categories {
              categoryId
              categoryName
              expectedParts
              actualParts
            }
          }
          categories {
            categoryId
            categoryName
            isScanRequired
            minStock
            currentUsableQty
            currentUsableParts
            currentPendingQty
            currentPendingParts
            periodAssignedQty
            periodExpectedParts
            periodActualDeductedParts
            periodGapParts
            periodOrderCount
            gapOrderIds
            gapOrderCount
          }
        }
      }
    }
  }
`;

export const NOTIFY_EMPLOYEE_INVENTORY_MISMATCH = gql`
  mutation notifyEmployeeInventoryMismatch($orderId: ID!) {
    notifyEmployeeInventoryMismatch(orderId: $orderId) {
      status
      message
      sentMessage
    }
  }
`;

export const REPORT_INVENTORY_LOSS = gql`
  mutation reportInventoryLoss($data: ReportInventoryLossInput!) {
    reportInventoryLoss(data: $data) {
      status
      message
      affectedCount
    }
  }
`;

export const GET_PRODUCT_SELECTION_GAPS = gql`
  query getProductSelectionGaps(
    $startDate: String!
    $endDate: String!
    $employeeId: ID
  ) {
    getProductSelectionGaps(
      startDate: $startDate
      endDate: $endDate
      employeeId: $employeeId
    ) {
      status
      message
      gaps {
        orderId
        bookingId
        bookingDate
        employeeId
        employeeName
        customerName
        serviceNames
        gapType
        productListSentAt
        productListApprovedAt
        productApprovalOverridden
        stockGaps {
          categoryName
          serviceName
          gap
          detail
        }
      }
    }
  }
`;

export const GET_RECIPE_DRIFT_SUGGESTIONS = gql`
  query getRecipeDriftSuggestions($startDate: String!, $endDate: String!) {
    getRecipeDriftSuggestions(startDate: $startDate, endDate: $endDate) {
      status
      message
      suggestions {
        serviceId
        serviceName
        categoryId
        categoryName
        orderCount
        totalUnitsAdded
        employeeIds
      }
    }
  }
`;

export const GET_EXPIRING_INVENTORY = gql`
  query getExpiringInventory($withinDays: Int) {
    getExpiringInventory(withinDays: $withinDays) {
      status
      message
      items {
        inventoryId
        name
        categoryName
        employeeId
        employeeName
        status
        expiryDate
        isExpired
      }
    }
  }
`;

export const GET_ORDERS_BY_PURCHASE_BATCH = gql`
  query getOrdersByPurchaseBatch($purchaseId: ID!) {
    getOrdersByPurchaseBatch(purchaseId: $purchaseId) {
      status
      message
      orders {
        orderId
        bookingId
        bookingDate
        employeeName
        customerName
        customerPhone
        categoryName
      }
    }
  }
`;

export const GET_INVENTORY_HISTORY = gql`
  query getInventoryHistory($inventoryId: ID!) {
    getInventoryHistory(inventoryId: $inventoryId) {
      status
      message
      data {
        id
        name
        status
        assignedEmployeeName
        assignedEmployeePhone
        assignedDate
        verifiedDate
        scans {
          orderId
          serviceName
          categoryName
          scannedAt
          scannedByAdmin
          forceScanned
        }
        assignmentHistory {
          action
          fromEmployeeName
          toEmployeeName
          fromStatus
          toStatus
          quantity
          changedByType
          note
          createdAt
        }
      }
    }
  }
`;

export const GET_WEEKLY_AUDIT_QUEUE = gql`
  query getWeeklyAuditQueue {
    getWeeklyAuditQueue {
      status
      message
      flagged {
        id
        employeeId
        employeeName
        categoryId
        categoryName
        auditCycleDate
        status
        employeeReportedQty
        employeeReportedFillEstimate
        systemExpectedQty
        systemExpectedParts
        diffQty
        createdAt
      }
      delinquencies {
        employeeId
        employeeName
        pendingCount
        oldestPendingDate
      }
    }
  }
`;

export const REVIEW_INVENTORY_AUDIT = gql`
  mutation reviewInventoryAudit($data: ReviewInventoryAuditInput!) {
    reviewInventoryAudit(data: $data) {
      status
      message
    }
  }
`;
